import React, { useEffect, useState } from "react";
import { Grid } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { setIsLoading } from "../../../store/slices/globalSlice";
import { getHeaders } from "./continerUtilityFunctions";
import PortfolioDashboard from "./portfolio_dashboard";
import HeaderComponent from "../Components/HeaderComponent";

import {
	setPageNumber,
	setTotalResults,
	setHasMoreResults,
	setPortfolioError,
	setPrevPageExists,
	setNextPageExists,
	setPortfolioProjects,
	setAllFilters,
	setAllFilterOptions,
} from "../../../store/slices/portfolioSlice";

const API_AUTH_HEADER = process.env.REACT_APP_API_AUTH_HEADER;
const API_KEY = process.env.REACT_APP_API_KEY;

const useReduxSelectors = () => {
	const csrfToken = useSelector((state) => state.global.csrfToken);
	const authenticated = useSelector((state) => state.global.authenticated);
	const tab = useSelector((state) => state.global.tab);
	const projectId = useSelector((state) => state.project.projectId);
	const hubDomain = useSelector((state) => state.global.hubDomain);
	const isLoading = useSelector((state) => state.global.isLoading);
	return { csrfToken, authenticated, tab, projectId, hubDomain, isLoading };
};

const PortfolioView = () => {
	const dispatch = useDispatch();
	const [firstTimeLoaded, setFirstTimeLoaded] = useState(false);

	const {
		selectedDivisions,
		selectedStages,
		selectedOpsLeaders,
		selectedProjectTypes,
		selectedAccounts,
		selectedPortfolioCompany,
		selectedMonth,
		sortField,
		pageNumber,
	} = useSelector((state) => state.portfolio);

	const {
		csrfToken,
		tab,
		hubDomain,
	} = useReduxSelectors();

	// Wrap handlePortfolioSearch in useCallback to prevent it from changing on every render
	const handlePortfolioSearch = React.useCallback(() => {
		const urlParams = new URLSearchParams(window.location.search);
		const user_uid = urlParams.get("user_uid");

		// Make sure we have a user_uid and we're in portfolioView mode
		if (user_uid && tab === "portfolio") {
			// Clear projects list and set loading state
			setPortfolioError(null);
			dispatch(setIsLoading(true));
			let headers = getHeaders(csrfToken, API_AUTH_HEADER, API_KEY); 

			// Build body for get_pv_data
			const body = {
				csrf_token: csrfToken,
				user_uid,
				sorting_order: sortField,
				page_number: pageNumber,
				company_filter:selectedPortfolioCompany?.length > 0? selectedPortfolioCompany: null,
				division_filter: selectedDivisions?.length > 0 ? selectedDivisions : null,
				stage_filter: selectedStages?.length > 0 ? selectedStages : null,
				ops_leader_filter: selectedOpsLeaders?.length > 0 ? selectedOpsLeaders : null,
				project_type_filter: selectedProjectTypes?.length > 0 ? selectedProjectTypes : null,
				account_filter: selectedAccounts?.length > 0 ? selectedAccounts : null,
				month_filter: selectedMonth && selectedMonth.length > 0 ? selectedMonth : null,
			};
			fetch(`${hubDomain}/phd/get_pv_data/`, {
				method: "POST",
				headers,
				body: JSON.stringify(body),
				credentials: "include",
			})
				.then((response) => {
					if (!response.ok) {
						setPortfolioError(
							`Error fetching data: Status ${response.status}). Our dev team is on it!`
						);
						dispatch(setIsLoading(false));
						return null;
					}
					return response.json();
				})
				.then((data) => {
					if (!data) {
						console.error("Failed setting Projects data:", data);
						dispatch(setIsLoading(false));
						return;
					}
					dispatch(setPortfolioProjects(data.projects || []));
					dispatch(setTotalResults(data.total_results || 0));
					dispatch(setPortfolioError(null));
					dispatch(setHasMoreResults(data.more_results || false));
					dispatch(setPrevPageExists(data.prev_page_exists || false));
					dispatch(setNextPageExists(data.next_page_exists || false));
					// Fill out dropdowns
					if (data.filter_dropdowns) {
						dispatch(setAllFilterOptions({
							portfolioCompanies: data.filter_dropdowns.companies || [],
							divisionOptions: data.filter_dropdowns.divisions || [],
							stageOptions: data.filter_dropdowns.stages || [],
							opsLeaderOptions: data.filter_dropdowns.ops_leaders || [],
							projectTypeOptions: data.filter_dropdowns.project_types || [],
							accountOptions: data.filter_dropdowns.accounts || [],
							monthOptions: data.filter_dropdowns.months || [],
						}));
						dispatch(setAllFilters({
							selectedPortfolioCompany: selectedPortfolioCompany || [],
							selectedDivisions: selectedDivisions || [],
							selectedStages: selectedStages || [],
							selectedOpsLeaders: selectedOpsLeaders || [],
							selectedProjectTypes: selectedProjectTypes || [],
							selectedAccounts: selectedAccounts || [],
							selectedMonth: selectedMonth || [],
							sortField: sortField,
						}));
					}
					dispatch(setIsLoading(false));
				})
				.catch((error) => {
					console.error("Error in fetching PV data:", error);
					dispatch(setIsLoading(false));
				});
		}
	},[tab,hubDomain, csrfToken, pageNumber, selectedPortfolioCompany, selectedDivisions, selectedStages, selectedOpsLeaders, selectedProjectTypes, selectedAccounts, selectedMonth, sortField, dispatch]);

	useEffect(() => {
		const filtersSelected =
			selectedPortfolioCompany?.length > 0 ||
			(selectedDivisions && selectedDivisions.length > 0) ||
			(selectedStages && selectedStages.length > 0) ||
			(selectedOpsLeaders && selectedOpsLeaders.length > 0) ||
			(selectedProjectTypes && selectedProjectTypes.length > 0) ||
			(selectedAccounts && selectedAccounts.length > 0);

		if (filtersSelected && pageNumber !== 1) {
			// Reset to page 1 when any filter is selected
			dispatch(setPageNumber(1));
		} else {
			// Initial load when no filters are selected
			handlePortfolioSearch();
			setFirstTimeLoaded(true);
		}
	}, [firstTimeLoaded, selectedPortfolioCompany, selectedDivisions, selectedStages, selectedOpsLeaders, selectedProjectTypes, selectedAccounts, sortField, pageNumber, dispatch, handlePortfolioSearch]);

	return (
		<div style={{ 
			height: '100vh', 
			display: 'flex', 
			flexDirection: 'column'
		}}>
			<div className="header-container" style={{ 
				flex: '0 0 auto'
			}}>
				<HeaderComponent
					handlePortfolioSearch={handlePortfolioSearch}
				/>
			</div>
			<div style={{
				flex: '1 1 0',
				minHeight: 0,
				overflow: 'hidden',
			}}>
				<PortfolioDashboard />
			</div>
		</div>
	);
};

export default PortfolioView;
